import z from "zod";

import fetcher from "@/lib/fetcher";
import ResourseError from "@/error/resourse-error";
import { actionClient } from "@/lib/safe-action";
import { objToCamelCase } from "@/lib/utils";

/**
 * Fetches the user profile from the server.
 *
 * @returns {Object|null} The user profile in camelCase format if successful, otherwise null.
 */
export async function getUserProfile() {
  const response = await fetcher("/api/app-content-ws/api/user/profile");
  if (!response.ok) return null;

  const { success, ...data } = await response.json();
  if (success) return objToCamelCase(data.data);

  return null;
}

/**
 * Fetches details for a given pincode.
 *
 * @param {string} pincode - The pincode for which details are to be fetched.
 * @returns {Object|null} The pincode details in camelCase format if successful, otherwise null.
 */
export async function getPincodeDetails(pincode) {
  const response = await fetcher(`/api/user-auth-ws/api/v1/pincode`, {
    method: "POST",
    params: { pincode },
  });

  if (!response.ok) return null;

  const { success, ...data } = await response.json();
  if (success) return objToCamelCase(data.data);

  return null;
}

/**
 * Updates the user profile with the given fields.
 *
 * @param {string} userId - The ID of the user whose profile is to be updated.
 * @param {Object} fields - An object containing the fields to update in the user profile.
 * @returns {Object|null} The updated user profile in camelCase format if successful, otherwise null.
 */
export async function updateProfile(userId, fields) {
  const response = await fetcher(
    `/api/user-auth-ws/api/v1/update/user/profile`,
    {
      method: "POST",
      body: JSON.stringify({
        ...fields,
        userId,
      }),
    }
  );

  if (!response.ok) return null;

  const { success, ...data } = await response.json();
  if (success) return objToCamelCase(data.data);

  return null;
}

export const sendOtpToUser = actionClient.action(async ({ clientInput }) => {
  const response = await fetcher("/api/user-auth-ws/v1/auth/send/otp", {
    method: "POST",
    body: JSON.stringify(clientInput),
  });

  if (!response.ok) {
    throw new ResourseError("Failed to send otp.");
  }

  // Return the data from the response
  const data = await response.json();
  if (data.success) return objToCamelCase(data.data);

  throw new ResourseError("Failed to send otp.");
});

export const loginRegisterUser = actionClient.action(
  async ({ clientInput }) => {
    const response = await fetcher("/api/user-auth-ws/v1/auth/user/entry", {
      method: "POST",
      body: JSON.stringify(clientInput),
    });

    if (!response.ok) {
      throw new ResourseError("Failed to login/register user.");
    }

    // Return the data from the response
    const data = await response.json();
    if (data.success) return objToCamelCase(data.data);

    throw new ResourseError(data?.msg || "Failed to login/register user.");
  }
);

export const examPreferences = actionClient.action(async () => {
  const response = await fetcher("/api/app-content-ws/api/exam/preference");

  if (!response.ok) {
    throw new ResourseError("Failed to fetch Exam Preferences.");
  }

  // Return the data from the response
  const data = await response.json();
  if (data.success) return objToCamelCase(data.data);

  throw new ResourseError("Failed to fetch Exam Preferences.");
});

export const saveUserExamPreferences = actionClient
  .inputSchema(
    z.object({
      categoryId: z.number().describe("Category ID"),
    })
  )
  .action(async ({ parsedInput }) => {
    const response = await fetcher("/api/app-content-ws/api/exam/preference", {
      method: "POST",
      body: JSON.stringify(parsedInput),
    });

    if (!response.ok) {
      throw new ResourseError("Failed to save Exam Preferences.");
    }

    // Return the data from the response
    const data = await response.json();
    if (data.success) return objToCamelCase(data.data);

    throw new ResourseError("Failed to save Exam Preferences.");
  });
